<template>
  <v-row>
    <v-col cols="1" class="align-vertical no-padding">
      {{ number }}
    </v-col>

    <v-col cols="2" class="align-vertical no-padding">
      <v-text-field 
        v-model="cameraName"
        @input="parseNewVal($event, 'name', `Cameras / Camera ${number} / Name`)"
      />
    </v-col>

    <v-col cols="1" class="align-vertical no-padding">
      <v-checkbox
        v-model="cameraEnabled"
        @change="parseNewVal($event, 'enabled', `Cameras / Camera ${number} / Enabled`)"
      />
    </v-col>

    <v-col cols="1" class="align-vertical no-padding">
      <v-checkbox
        v-model="cameraMirrored"
        @change="parseNewVal($event, 'mirrored', `Cameras / Camera ${number} / Mirrored`)"
      />
    </v-col>

    <v-col cols="1" class="align-vertical no-padding">
      <v-checkbox
        v-model="cameraFlipped"
        @change="parseNewVal($event, 'flipped', `Cameras / Camera ${number} / Flipped`)"
      />
    </v-col>

    <v-col cols="2" class="align-vertical no-padding">
      <v-select
        v-model="cameraMSQ"
        :items="steamQualityMap"
        item-text="label"
        @change="parseNewVal($event, 'MSQ', `Cameras / Camera ${number} / Main Strem Quality`)"
      />
    </v-col>

    <v-col cols="2" class="align-vertical no-padding">
      <v-select
        v-model="cameraSSQ"
        :items="steamQualityMap"
        item-text="label"
        @change="parseNewVal($event, 'SSQ', `Cameras / Camera ${number} / Sub Stream Quality`)"
      />
    </v-col>

    <v-col :cols="dynamicCol" class="align-vertical no-padding">
      <v-checkbox
        v-model="cameraDisableACC"
        @change="parseNewVal($event, 'disableACC', `Cameras / Camera ${number} / Disable with ACC`)"
      />
    </v-col>

    <v-col v-if="supportsAudioSettings" cols="1" class="align-vertical no-padding">
      <v-checkbox
        v-model="audioEnabled"
        :disabled="disableCameraOption"
        @change="parseNewVal($event, 'audioSettings', `Cameras / Camera ${number} / Enable Audio`)"
      />
    </v-col>
  </v-row>
</template>

<script>

import {deviceConfigMixin} from '@/mixins/deviceConfigMixin.js';

import { Logger } from "aws-amplify";
const logger = new Logger("Camera Settings");

export default {
  name: 'CameraSettings',
  mixins:  [ deviceConfigMixin ],
  props: {
    camera: {
      type: Object,
      required: true
    },
    supportsAudioSettings: {
      type: Boolean,
      required: true
    },
    audioEnabledCount: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      cameraName: '',
      cameraEnabled: Boolean,
      cameraMirrored: Boolean,
      cameraFlipped: Boolean,
      cameraDisableACC: Boolean,
      cameraMSQ: null,
      cameraSSQ: null,
      audioEnabled: Boolean,
      steamQualityMap: [
        {
          xml: 'high',
          label: 'High'
        },
        {
          xml: 'normal',
          label: 'Normal'
        }
      ]
    }
  },
  computed: {
    number() {
      return this.camera.number[0];
    },
    name() {
      return this.camera.name[0];
    },
    enabled() {
      return JSON.parse(this.camera.enabled[0]);
    },
    mirrored() {
      return JSON.parse(this.camera.mirror[0]);
    },
    flipped() {
      return JSON.parse(this.camera.flip[0]);
    },
    MSQ() {
      return this.camera.mainStreamQual[0];
    },
    SSQ() {
      return this.camera.subStreamQual[0];
    },
    disableACC() {
      return JSON.parse(this.camera.stopRecOnAcc[0]);
    },
    audioSettings() {
      return this.camera.audioCapEnabled ? JSON.parse(this.camera.audioCapEnabled[0]) : false;
    },
    dynamicCol() {
      return this.supportsAudioSettings ? 1 : 2;
    },
    disableCameraOption() {
      return this.audioEnabledCount > 3 && !this.audioEnabled ? true : false
    }
  },
  watch: {
    camera: {
      deep: true,
      immediate: true,
      handler() {
        logger.info('camera updated ', this.camera)
        this.mapProps()
      }
    }
  },
  mounted() {
    this.mapProps();
    
  },
  methods: {
    mapProps() {
      this.cameraName = this.name;
      this.cameraEnabled = this.enabled;
      this.cameraMirrored = this.mirrored;
      this.cameraFlipped = this.flipped;
      this.cameraMSQ = this.steamQualityMap.find(item => {
        return item.xml === this.MSQ;
      });
      this.cameraSSQ = this.steamQualityMap.find(item => {
        return item.xml === this.SSQ;
      });
      this.cameraDisableACC = this.disableACC;
      this.audioEnabled = this.audioSettings;
    },
    parseNewVal(val, id, label) {
      let payload = {
        newValue: this.returnMappedVal(val, [this.steamQualityMap]),
        key: this.configPropMapper(id),
        label: label,
        context: {
          newValue: this.number,
          key: this.configPropMapper('number'),
          label: label
        }
      }

      this.$emit('settingsUpdated', payload, this.steamQualityMap);
    },
    configPropMapper(key) {
      let keyBase = `device_config.video[0].camera[0].channel[${this.number - 1}]`;
      let map = {
        'number': `${keyBase}.number[0]`,
        'name': `${keyBase}.name[0]`,
        'enabled': `${keyBase}.enabled[0]`,
        'mirrored': `${keyBase}.mirror[0]`,
        'flipped': `${keyBase}.flip[0]`,
        'MSQ': `${keyBase}.mainStreamQual[0]`,
        'SSQ': `${keyBase}.subStreamQual[0]`,
        'disableACC': `${keyBase}.stopRecOnAcc[0]`,
        'audioSettings':  `${keyBase}.audioCapEnabled[0]`
      }

      return map[key];
    }
  }
}
</script>

<style lang="scss" scoped>
  .align-vertical {
    justify-content: left;
    align-items: center;
    display: flex;
  }
  .no-padding {
    padding: .5em !important;
    margin: 0 !important;
  }
</style>